import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { LineChart, Line, CartesianGrid, ResponsiveContainer } from 'recharts'
import { sampleECGData } from '../utils/sampleECGData'

interface PrintECGLeadProps {
  name: string
  data: number[]
  highlight?: boolean
  height?: number
  isRhythmStrip?: boolean
}

/**
 * Componente de derivação ECG otimizado para impressão usando Recharts
 * SVG vetorial para qualidade perfeita no PDF
 */
export const PrintECGLead = ({
  name,
  data,
  highlight = false,
  height = 100,
  isRhythmStrip = false
}: PrintECGLeadProps) => {
  const theme = useTheme()

  // Lead de ritmo usa 10s, demais leads usam 2.5s (padrão)
  const durationSeconds = isRhythmStrip ? 10 : 2.5
  const chartData = sampleECGData(data, durationSeconds)

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: `${height}px`,
        backgroundColor: theme.palette.background.paper,
        pageBreakInside: 'avoid',
        border: `1px solid ${
          highlight ? theme.palette.ecgAlert.main : theme.palette.ecgNormal.main
        }`
      }}
    >
      {/* Grid de fundo ECG */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(to right, ${theme.palette.ecgGridThin.main} 1px, transparent 1px),
            linear-gradient(to bottom, ${theme.palette.ecgGridThin.main} 1px, transparent 1px),
            linear-gradient(to right, ${theme.palette.ecgGridThick.main} 1px, transparent 1px),
            linear-gradient(to bottom, ${theme.palette.ecgGridThick.main} 1px, transparent 1px)
          `,
          backgroundSize: '5px 5px, 5px 5px, 25px 25px, 25px 25px',
          zIndex: 0
        }}
      />

      {/* Label da derivação - sobreposto */}
      <Typography
        variant='caption'
        sx={{
          position: 'absolute',
          top: '4px',
          left: '4px',
          fontWeight: highlight ? 'bold' : 'normal',
          color: '#000000 !important',
          fontSize: '0.7rem',
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(30, 30, 30, 0.85)'
              : 'rgba(250, 250, 250, 0.85)',
          lineHeight: 1,
          zIndex: 2
        }}
      >
        {name}
      </Typography>

      {/* Gráfico Recharts */}
      <ResponsiveContainer width='100%' height='100%'>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
        >
          <CartesianGrid stroke='transparent' />
          <Line
            type='monotone'
            dataKey='voltage'
            stroke={theme.palette.ecgStroke.main}
            dot={false}
            strokeWidth={1.5}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  )
}
